import MemberButton from '@/member/components/MemberButton'
import { useOrgContext } from '@/org/contexts/OrgContext'
import {
  Box,
  Button,
  ButtonGroup,
  HStack,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd'
import { MeetingStepDataTasks } from '@rolebase/shared/model/meeting_step'
import {
  RoleReviewBy,
  roleReviewLabelKey,
  RoleReviewMember,
  roleReviewMembers,
  RoleReviewRole,
  roleReviewRoles,
  roleReviewTextKey,
} from '@rolebase/shared/model/roleReview'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import MeetingRoleReviewItem from './MeetingRoleReviewItem'
import type { TasksDataPatch } from './MeetingStepContentTasks'

interface Props {
  stepId: string
  circleId: string
  data: MeetingStepDataTasks
  readOnly?: boolean
  onChange(patch: TasksDataPatch): void
}

const reviewByModes: RoleReviewBy[] = ['Member', 'Role']

// The "Texts" mode of the Projects step (#80): one text per person, in a
// speaking order anyone can change, or one per role, in the org chart's order
// with the vacant roles last. Both series are kept; switching loses nothing.
export default function MeetingRoleReview({
  stepId,
  circleId,
  data,
  readOnly,
  onChange,
}: Props) {
  const { t } = useTranslation()
  const { orgData } = useOrgContext()
  // Once the meeting has ended, the switch still lets anyone read the other
  // series, without writing anything
  const [readBy, setReadBy] = useState<RoleReviewBy>(data.reviewBy ?? 'Member')
  const by = readOnly ? readBy : data.reviewBy ?? 'Member'
  const handleBy = (mode: RoleReviewBy) =>
    readOnly ? setReadBy(mode) : onChange({ reviewBy: mode })

  const roles = useMemo(
    () => (orgData ? roleReviewRoles(orgData, circleId) : []),
    [orgData, circleId]
  )
  const members = useMemo(
    () =>
      orgData
        ? roleReviewMembers(orgData, circleId, data.reviewMembersOrder)
        : [],
    [orgData, circleId, data.reviewMembersOrder]
  )

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      const to = result.destination?.index
      if (to === undefined || to === result.source.index) return
      const ids = members.map((m) => m.member.id)
      const [moved] = ids.splice(result.source.index, 1)
      ids.splice(to, 0, moved)
      onChange({ reviewMembersOrder: ids })
    },
    [members, onChange]
  )

  const textOf = (kind: 'member' | 'role', id: string) =>
    data[roleReviewTextKey(kind, id)] ?? ''
  // Each text is saved with its label, frozen as the roles stand now, so the
  // summary reads the meeting as it was (see roleReviewEntries)
  const saveMember =
    (member: RoleReviewMember['member'], memberRoles: RoleReviewRole[]) =>
    (value: string) => {
      const rolesNames = memberRoles.map((row) => row.role.name).join(', ')
      onChange({
        [roleReviewTextKey('member', member.id)]: value,
        [roleReviewLabelKey(
          'member',
          member.id
        )]: `${member.name} (${rolesNames})`,
      } as TasksDataPatch)
    }
  const saveRole = (row: RoleReviewRole) => (value: string) => {
    const holdersNames =
      row.holders.map((holder) => holder.name).join(', ') ||
      t('MeetingRoleReview.nobody')
    onChange({
      [roleReviewTextKey('role', row.circle.id)]: value,
      [roleReviewLabelKey(
        'role',
        row.circle.id
      )]: `${row.role.name} (${holdersNames})`,
      reviewRolesOrder: roles.map((r) => r.circle.id),
    } as TasksDataPatch)
  }

  // Once the meeting has ended, an empty card says nothing
  const shown = (kind: 'member' | 'role', id: string) =>
    !readOnly || textOf(kind, id).trim() !== ''

  const heldRoles = roles.filter((row) => row.holders.length > 0)
  const vacantRoles = roles.filter((row) => row.holders.length === 0)

  return (
    <Box>
      <ButtonGroup size="sm" isAttached variant="outline" mb={4}>
        {reviewByModes.map((mode) => (
          <Button
            key={mode}
            isActive={mode === by}
            onClick={() => mode !== by && handleBy(mode)}
          >
            {t(`MeetingRoleReview.by${mode}`)}
          </Button>
        ))}
      </ButtonGroup>

      {roles.length === 0 && (
        <Text fontStyle="italic" color="gray.500">
          {t('MeetingRoleReview.empty')}
        </Text>
      )}

      {by === 'Member' && (
        <>
          {!readOnly && members.length > 1 && (
            <Box mb={4}>
              <Text fontSize="sm" color="gray.500" mb={1}>
                {t('MeetingRoleReview.order')}
              </Text>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable
                  droppableId={`role-review-${stepId}`}
                  direction="horizontal"
                >
                  {(provided) => (
                    <Wrap
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      spacing={1}
                    >
                      {members.map(({ member }, index) => (
                        <Draggable
                          key={member.id}
                          draggableId={`role-review-${stepId}-${member.id}`}
                          index={index}
                        >
                          {(dragProvided) => (
                            <WrapItem
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              cursor="grab"
                            >
                              <HStack spacing={1}>
                                <Text fontSize="xs" color="gray.500">
                                  {index + 1}
                                </Text>
                                {/* Not a target of its own: the drag library
                                    refuses to start a drag on a <button>
                                    (same parade as the tensions, #106) */}
                                <MemberButton
                                  member={member}
                                  size="sm"
                                  pointerEvents="none"
                                  tabIndex={-1}
                                />
                              </HStack>
                            </WrapItem>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Wrap>
                  )}
                </Droppable>
              </DragDropContext>
            </Box>
          )}

          {members
            .filter(({ member }) => shown('member', member.id))
            .map(({ member, roles: memberRoles }) => (
              <MeetingRoleReviewItem
                key={member.id}
                docId={`meeting-step-${stepId}-review-member-${member.id}`}
                member={member}
                roles={memberRoles}
                value={textOf('member', member.id)}
                readOnly={readOnly}
                onSave={saveMember(member, memberRoles)}
              />
            ))}
        </>
      )}

      {by === 'Role' && (
        <>
          {heldRoles
            .filter((row) => shown('role', row.circle.id))
            .map((row) => (
              <MeetingRoleReviewItem
                key={row.circle.id}
                docId={`meeting-step-${stepId}-review-role-${row.circle.id}`}
                roles={[row]}
                holders={row.holders}
                value={textOf('role', row.circle.id)}
                readOnly={readOnly}
                onSave={saveRole(row)}
              />
            ))}

          {vacantRoles.some((row) => shown('role', row.circle.id)) && (
            <Text fontSize="sm" color="gray.500" mt={6} mb={2}>
              {t('MeetingRoleReview.vacant')}
            </Text>
          )}
          {vacantRoles
            .filter((row) => shown('role', row.circle.id))
            .map((row) => (
              <MeetingRoleReviewItem
                key={row.circle.id}
                docId={`meeting-step-${stepId}-review-role-${row.circle.id}`}
                roles={[row]}
                holders={[]}
                value={textOf('role', row.circle.id)}
                readOnly={readOnly}
                onSave={saveRole(row)}
              />
            ))}
        </>
      )}
    </Box>
  )
}
